/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { usePortalSession } from '../../context/PortalContext';
import axios from '../../api/axiosClient';
import toast from '../../utils/toast';
import { motion } from 'framer-motion';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const MyProfilePage = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation('booking');
  const { portalData, updatePortalData } = usePortalSession();
  
  const [phone, setPhone] = useState('');
  const [preferredBarberId, setPreferredBarberId] = useState(null);
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Initialize form data from portal data
  useEffect(() => {
    if (portalData) {
      setPhone(portalData.client?.phone || '');
      setPreferredBarberId(portalData.client?.preferred_barber_user_id || null);
    }
  }, [portalData]);

  // Load barbers for the dropdown
  useEffect(() => {
    const loadBarbers = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/portal/barbers');
        setBarbers(response.data.barbers);
      } catch (error) {
        console.error('Failed to load barbers:', error);
        toast.error('Failed to load barbers');
      } finally {
        setLoading(false);
      }
    };

    loadBarbers();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const result = await updatePortalData({
        phone: phone.trim() || null,
        preferred_barber_user_id: preferredBarberId || null
      });
      
      if (result.success) {
        toast.success('Profile updated successfully');
        
        // Fire analytics event
        if (window.gtag) {
          window.gtag('event', 'profile_updated', {
            phone_updated: !!phone.trim(),
            preferred_barber_updated: !!preferredBarberId
          });
        }
      } else {
        toast.error(result.error || 'Failed to update profile');
      }
      
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const validatePhone = (phoneNumber) => {
    if (!phoneNumber.trim()) return true; // Empty is allowed
    
    // Simple phone validation - can be enhanced
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phoneNumber.replace(/[\s\-()]/g, ''));
  };

  const filteredBarbers = barbers.filter(barber =>
    barber.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (barber.specialties && barber.specialties.some(specialty =>
      specialty.toLowerCase().includes(searchQuery.toLowerCase())
    ))
  );

  const selectedBarber = barbers.find(b => b.id === preferredBarberId);

  if (!portalData) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 w-32 bg-gray-300 rounded mb-4"></div>
          <div className="space-y-4">
            <div className="h-4 w-48 bg-gray-300 rounded"></div>
            <div className="h-4 w-56 bg-gray-300 rounded"></div>
            <div className="h-4 w-40 bg-gray-300 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const hasChanges = 
    phone !== (portalData.client?.phone || '') ||
    preferredBarberId !== (portalData.client?.preferred_barber_user_id || null);

  const isValid = validatePhone(phone);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center text-zinc-900 dark:text-zinc-100">
              <svg className="h-6 w-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {t('booking.my_profile_title')}
            </h1>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
              {t('booking.profile_manage_account')}
            </p>
          </div>
        </div>
      </div>

      <Card>
        
        <CardHeader
          title="Account Information"
          subtitle="Your basic account details"
          icon={
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        
        <div className="p-4 md:p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-zinc-700 dark:text-zinc-300">
              Name
            </label>
            <div className="p-3 rounded-xl bg-zinc-100 dark:bg-zinc-800/50 text-zinc-900 dark:text-zinc-100 border border-white/10 dark:border-white/10">
              {portalData.user?.name || 'Not provided'}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-zinc-700 dark:text-zinc-300">
              {t('booking.portal_email')}
            </label>
            <div className="p-3 rounded-xl bg-zinc-100 dark:bg-zinc-800/50 text-zinc-900 dark:text-zinc-100 border border-white/10 dark:border-white/10">
              {portalData.user?.email || 'Not provided'}
            </div>
          </div>
        </div>

        {/* Editable Information */}
        <CardHeader
          title="Contact Preferences"
          subtitle="Manage your contact information"
          icon={
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          }
        />
        
        <div className="p-4 md:p-5 space-y-6">
          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-700 dark:text-zinc-300">
              {t('booking.portal_phone_number')}
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter your phone number"
              className={`w-full px-4 py-3 rounded-xl border transition-colors duration-200 ${
                isValid
                  ? 'bg-white/50 dark:bg-zinc-800/50 border-white/20 dark:border-white/10 text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 dark:placeholder-zinc-400 focus:border-[--accent]/30 focus:ring-[--accent]/20'
                  : 'bg-white/50 dark:bg-zinc-800/50 border-red-500/50 text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 dark:placeholder-zinc-400 focus:border-red-500/50 focus:ring-red-500/20'
              } backdrop-blur-sm`}
              style={{ '--accent': ACCENT }}
            />
            {!isValid && phone.trim() && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {t('booking.profile_phone_invalid')}
              </p>
            )}
            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
              {t('booking.profile_phone_hint')}
            </p>
          </div>

          {/* Preferred Barber */}
          <div>
            <label className="block text-sm font-medium mb-2 text-zinc-700 dark:text-zinc-300">
              {t('booking.profile_preferred_barber')}
            </label>
            
            {loading ? (
              <div className="p-3 rounded-xl bg-zinc-100 dark:bg-zinc-800/50 border border-white/10 dark:border-white/10">
                <div className="animate-pulse">
                  <div className="h-4 w-32 bg-zinc-300 rounded"></div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Search Input */}
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search barbers..."
                  className="w-full px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 dark:placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                  style={{ '--accent': ACCENT }}
                />
                  
                {/* Selected Barber Display */}
                {selectedBarber && (
                  <div className="p-3 rounded-xl border-2 bg-amber-50 border-amber-200 dark:bg-[rgba(125,102,51,.15)] dark:border-[rgba(125,102,51,.35)]">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm"
                        style={{ backgroundColor: selectedBarber.color_hex || '#3B82F6' }}
                      >
                        {selectedBarber.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-amber-800 dark:text-amber-300">
                          {selectedBarber.name}
                        </div>
                        {selectedBarber.specialties && selectedBarber.specialties.length > 0 && (
                          <div className="text-xs text-amber-700 dark:text-amber-300">
                            {selectedBarber.specialties.join(', ')}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => setPreferredBarberId(null)}
                        className="p-1 rounded transition-colors text-amber-700 hover:text-amber-800 dark:text-amber-300 dark:hover:text-amber-200"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
                  
                {/* Barber Options */}
                {filteredBarbers.length > 0 && (
                  <div className="max-h-48 overflow-y-auto rounded-xl border border-white/20 dark:border-white/10">
                    {filteredBarbers.map(barber => (
                      <button
                        key={barber.id}
                        onClick={() => {
                          setPreferredBarberId(barber.id);
                          setSearchQuery('');
                        }}
                        className={`w-full p-3 text-left hover:bg-white/30 dark:hover:bg-zinc-800/30 transition-colors ${
                          preferredBarberId === barber.id
                            ? 'bg-amber-50 dark:bg-[rgba(125,102,51,.15)]'
                            : ''
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-xs"
                            style={{ backgroundColor: barber.color_hex || '#3B82F6' }}
                          >
                            {barber.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-zinc-900 dark:text-zinc-100">
                              {barber.name}
                            </div>
                            {barber.specialties && barber.specialties.length > 0 && (
                              <div className="text-xs text-zinc-500 dark:text-zinc-400">
                                {barber.specialties.join(', ')}
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                
                {filteredBarbers.length === 0 && searchQuery && (
                  <div className="p-3 text-center text-zinc-500 dark:text-zinc-400">
                    No barbers found matching "{searchQuery}"
                  </div>
                )}
                
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  {t('booking.profile_preferred_barber_hint')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="p-4 md:p-5 border-t border-white/10 dark:border-white/10">
          <div className="flex justify-end">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSave}
              disabled={!hasChanges || !isValid || saving}
              className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                hasChanges && isValid && !saving
                  ? 'text-white'
                  : 'cursor-not-allowed opacity-50'
              }`}
              style={hasChanges && isValid && !saving ? { backgroundColor: ACCENT } : {}}
            >
              {saving ? t('admin.saving') : t('booking.profile_save')}
            </motion.button>
          </div>
        </div>

        {/* Info Notice */}
        <div className="p-4 md:p-5 border-t border-white/10 dark:border-white/10">
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 dark:bg-[rgba(125,102,51,.15)] dark:border-[rgba(125,102,51,.35)]">
            <div className="flex items-start space-x-3">
              <svg className="h-5 w-5 mt-0.5 text-amber-600 dark:text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="font-medium text-amber-800 dark:text-amber-300">
                  {t('booking.profile_benefits')}
                </h3>
                <div className="text-sm mt-1 space-y-1 text-amber-700 dark:text-amber-200">
                  <p>• Your preferred barber will be pre-selected when booking</p>
                  <p>• Phone number enables SMS appointment reminders</p>
                  <p>• Changes are saved automatically across all devices</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default MyProfilePage;
